import React, { useState, useMemo, useEffect } from "react"
import styled, { css } from '~/ui/styled'
import FastImage from "~/ui/FastImage"
import { AvatarWrapperTypes, AvatarProps, AvatarTypes, ImageAvatarProps, TextAvatarProps, IconAvatarProps } from "./types"
import { CenterFlex, IconFont } from "~components/El"
import { resolveAvatarUri } from "./resolveAvatarUri"

const getRadius = (size: number, type: AvatarWrapperTypes, squareRadius?: number ) => {
  if (type === AvatarWrapperTypes.CIRCLE) return size / 2
  return squareRadius ?? 6
}


type AvatarWrapProps = {
  size: number
  radius: number
  backgroundColor?: string
  width?: number | string
  height?: number | string
}

const avatarWrapCss = css<AvatarWrapProps>`
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${({ radius }) => radius}px;
  background-color: ${({ theme, backgroundColor }) => backgroundColor || theme.colors.success};
  width: ${({ width, size }) => (width != null ? width : `${size}px`)};
  height: ${({ height, size }) => (height != null ? height : `${size}px`)};
`

const BaseWrap = styled(CenterFlex)<AvatarWrapProps>`
flex-shrink:0;
  ${avatarWrapCss}
`

const BasePressableWrap = styled(CenterFlex)<AvatarWrapProps>`
flex-shrink:0;
  ${avatarWrapCss}
`


const Image = styled(FastImage)<Pick<ImageAvatarProps, "size">>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`

const Text = styled.Text<{ size: number; color: string }>`
  font-size: ${({ size }) => size || 14}px;
  color: ${({ theme, color }) => color || theme.colors.textLight};
  font-weight: 600;
`
const ImageAvatar: React.FC<ImageAvatarProps> = ({ uri, size, onError, onLoadEnd }) => {
  const resolvedUri = resolveAvatarUri(uri)
  if (!resolvedUri) return null
  return <Image src={resolvedUri} size={size} onError={onError} onLoadEnd={onLoadEnd} />
}
const TextAvatar: React.FC<TextAvatarProps> = ({ name, size, color }) => {
  return <Text size={size} color={color}>{name ? name[0] : ""}</Text>
}

const IconAvatar: React.FC<IconAvatarProps> = ({ icon, size = 16, color }) => {
  return <IconFont name={icon} color={color} size={size} />
}

const Avatar: React.FC<AvatarProps> = ({
  avatar,
  icon,
  name,
  size = 40,
  color = "#fff",
  type = AvatarWrapperTypes.CIRCLE,
  squareRadius,
  backgroundColor,
  onClick,
  width,
  height,
}) => {
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setError(false)
    setLoading(false)
  }, [avatar])

  const avatarType = useMemo(() => {
    if (avatar && !error) return AvatarTypes.IMAGE
    if (icon) return AvatarTypes.ICON
    return AvatarTypes.TEXT
  }, [avatar, error, icon])

  const radius = useMemo(
    () => getRadius(size, type, squareRadius),
    [size, type, squareRadius],
  )

  const Wrapper: any = onClick ? BasePressableWrap : BaseWrap

  return (
    <Wrapper
      size={size}
      minWidth={'max-content'}
      width={width}
      height={height}
      radius={radius}
      backgroundColor={backgroundColor}
      onClick={onClick}
    >
      {avatarType === AvatarTypes.IMAGE && (
        <ImageAvatar
          uri={avatar!}
          size={size}
          onLoadEnd={() => setLoading(false)}
          onError={() => {
            setLoading(false)
            setError(true)
          }}
        />
      )}

      {loading && (<IconFont name="loanding" size={Math.floor(size * 0.4)} color={color} />)}

      {avatarType === AvatarTypes.ICON && (
        <IconAvatar
          icon={icon}
          size={Math.floor(size * 0.4)}
          color={color}
        />
      )}

      {avatarType === AvatarTypes.TEXT && (
        <TextAvatar
          name={name}
          size={Math.floor(size * 0.4)}
          color={color}
        />
      )}
    </Wrapper>
  )
}
export default Avatar
